import { isEmpty, isEqual } from 'lodash'
import { useCallback } from 'react'
import {
  FieldArrayProps,
  useFieldArray as useFinalFieldArray,
  UseFieldArrayConfig,
} from 'react-final-form-arrays'

import { useFieldContext } from '../Field'
import { TMeta } from '../Field/types'

interface TUseFieldArrayOption extends UseFieldArrayConfig<any> {
  ignorePrefix?: boolean
  required?: boolean
}

interface TReturnUseField<T> extends Pick<FieldArrayProps<T, any>, 'fields'> {
  meta: TMeta
  isEmpty: boolean
}

const useFieldArray = <T>(
  name: string,
  options: TUseFieldArrayOption = {},
): TReturnUseField<T> => {
  const { ignorePrefix, required, subscription, ...moreOptions } = options

  const { prefix } = useFieldContext()

  let fieldName = name

  if (prefix && !ignorePrefix) {
    fieldName = `${prefix}.${fieldName}`
  }

  const validate = useCallback(
    (v) => {
      if (required) {
        if (isEmpty(v)) {
          return 'isEmpty'
        }
        return undefined
      }
      return undefined
    },
    [required],
  )

  const {
    fields,
    meta,
    meta: { error, touched = false },
  } = useFinalFieldArray<T>(fieldName, {
    validate,
    subscription: { error: true, touched: true, ...subscription },
    ...moreOptions,
  })

  return {
    fields: fields,
    meta: meta,
    isEmpty: touched && isEqual(error, 'isEmpty'),
  }
}

export default useFieldArray
